import clsx from "clsx";
import { useEffect, useState } from "react";
import { MButton, FullScreenModal } from "../../../components/mobile";
import { INhomLop } from "../../../model/respone/classes/INhomLop";
import { IKyDangKyOption } from "../../../model/respone/classes/IKyDangKy";

interface IFilterModalProps {
    isOpen: boolean;
    onClose: () => void;
    /** Giá trị filter hiện tại (controlled bởi ClassesPage). */
    hinhThuc: number;
    nhomLop: string;
    kyDangKy: number;
    /** Catalog nhóm lớp đợt hiện tại. */
    nhomLopOptions: INhomLop[];
    /** Options đợt học — chỉ render khi showKyDangKyCombo (đợt mở có > 1 ky_dang_ky). */
    kyDangKyOptions: IKyDangKyOption[];
    showKyDangKyCombo: boolean;
    /** Áp dụng — đẩy ngược 3 giá trị lên ClassesPage. */
    onApply: (hinhThuc: number, nhomLop: string, kyDangKy: number) => void;
}

/** Hình thức đăng ký — khớp option desktop. */
const HINH_THUC_OPTIONS = [
    { value: 0, label: "Tất cả hình thức" },
    { value: 1, label: "Học chính khóa" },
    { value: 2, label: "Học lại, cải thiện điểm, học bù (lớp riêng)" },
    { value: 3, label: "Học lại, cải thiện điểm, học bù (lớp ghép)" },
];

/**
 * Bộ lọc tràn màn hình cho mobile — gom 2 select desktop (hình thức + nhóm lớp).
 * Giữ state nháp riêng, chỉ đẩy lên ClassesPage khi bấm "Áp dụng".
 */
const FilterModal = ({ isOpen, onClose, hinhThuc, nhomLop, kyDangKy, nhomLopOptions, kyDangKyOptions, showKyDangKyCombo, onApply }: IFilterModalProps) => {
    const [draftHinhThuc, setDraftHinhThuc] = useState(hinhThuc);
    const [draftNhomLop, setDraftNhomLop] = useState(nhomLop);
    const [draftKyDangKy, setDraftKyDangKy] = useState(kyDangKy);

    // Đồng bộ state nháp với giá trị thật mỗi lần mở modal.
    useEffect(() => {
        if (isOpen) {
            setDraftHinhThuc(hinhThuc);
            setDraftNhomLop(nhomLop);
            setDraftKyDangKy(kyDangKy);
        }
    }, [isOpen, hinhThuc, nhomLop, kyDangKy]);

    const apply = () => {
        onApply(draftHinhThuc, draftNhomLop, draftKyDangKy);
        onClose();
    };

    const reset = () => {
        setDraftHinhThuc(0);
        setDraftNhomLop("");
        setDraftKyDangKy(0);
    };

    return (
        <FullScreenModal
            isOpen={isOpen}
            title="Bộ lọc môn học"
            onClose={onClose}
            footer={
                <>
                    <MButton variant="default" block onClick={reset}>
                        Xóa lọc
                    </MButton>
                    <MButton variant="primary" block onClick={apply}>
                        Áp dụng
                    </MButton>
                </>
            }
        >
            <div className="m-filter-group">
                <div className="m-filter-group-title">Hình thức đăng ký</div>
                {HINH_THUC_OPTIONS.map(opt => (
                    <label
                        key={opt.value}
                        className={clsx(
                            "m-filter-option",
                            draftHinhThuc === opt.value && "m-filter-option-selected"
                        )}
                    >
                        <input
                            type="radio"
                            name="m-filter-hinhthuc"
                            checked={draftHinhThuc === opt.value}
                            onChange={() => setDraftHinhThuc(opt.value)}
                        />
                        {opt.label}
                    </label>
                ))}
            </div>

            <div className="m-filter-group">
                <div className="m-filter-group-title">Nhóm lớp</div>
                <label
                    className={clsx(
                        "m-filter-option",
                        draftNhomLop === "" && "m-filter-option-selected"
                    )}
                >
                    <input
                        type="radio"
                        name="m-filter-nhomlop"
                        checked={draftNhomLop === ""}
                        onChange={() => setDraftNhomLop("")}
                    />
                    Tất cả nhóm lớp
                </label>
                {nhomLopOptions.map(nl => (
                    <label
                        key={nl.id}
                        className={clsx(
                            "m-filter-option",
                            draftNhomLop === String(nl.id) && "m-filter-option-selected"
                        )}
                    >
                        <input
                            type="radio"
                            name="m-filter-nhomlop"
                            checked={draftNhomLop === String(nl.id)}
                            onChange={() => setDraftNhomLop(String(nl.id))}
                        />
                        {nl.ten_nhom}
                    </label>
                ))}
            </div>

            {/* Đợt học — chỉ hiện khi đợt mở có nhiều hơn 1 ky_dang_ky. */}
            {showKyDangKyCombo && (
                <div className="m-filter-group">
                    <div className="m-filter-group-title">Đợt học</div>
                    <label
                        className={clsx(
                            "m-filter-option",
                            draftKyDangKy === 0 && "m-filter-option-selected"
                        )}
                    >
                        <input
                            type="radio"
                            name="m-filter-kydangky"
                            checked={draftKyDangKy === 0}
                            onChange={() => setDraftKyDangKy(0)}
                        />
                        Tất cả đợt học
                    </label>
                    {kyDangKyOptions.map(k => (
                        <label
                            key={k.ky_dang_ky}
                            className={clsx(
                                "m-filter-option",
                                draftKyDangKy === k.ky_dang_ky && "m-filter-option-selected"
                            )}
                        >
                            <input
                                type="radio"
                                name="m-filter-kydangky"
                                checked={draftKyDangKy === k.ky_dang_ky}
                                onChange={() => setDraftKyDangKy(k.ky_dang_ky)}
                            />
                            {k.label}
                        </label>
                    ))}
                </div>
            )}
        </FullScreenModal>
    );
};

export default FilterModal;
